import { Controller } from "@hotwired/stimulus"

// Toggles the app between auto/light/dark themes.
//
// On click/change, this controller:
//   1. updates document.documentElement[data-theme-preference]
//   2. resolves "auto" against window.matchMedia(prefers-color-scheme)
//   3. sets data-bs-theme so Bootstrap picks up the new palette
//   4. writes a 1-year `theme` cookie so it survives sign-out / device hops
//   5. POSTs to /account/update_theme when the user is signed in, so the
//      preference also lives on the User record
//
// Used by:
//   - app/views/accounts/show.html.slim   (select that fires on change)
//   - app/views/shared/_navbar.html.slim  (three dropdown items with
//     data-action="click->theme#change" and data-theme-theme-param)
export default class extends Controller {
  static values = { signedIn: { type: Boolean, default: false } }

  connect() {
    this.mediaQuery = window.matchMedia("(prefers-color-scheme: dark)")
    this.osChangeHandler = () => this.applyResolved()
    this.mediaQuery.addEventListener("change", this.osChangeHandler)
  }

  disconnect() {
    this.mediaQuery?.removeEventListener("change", this.osChangeHandler)
  }

  change(event) {
    const theme = event.params?.theme ?? event.target.value
    if (!["auto", "light", "dark"].includes(theme)) return

    document.documentElement.dataset.themePreference = theme
    this.applyResolved()
    this.setCookie(theme)
    if (this.signedInValue) this.persistToServer(theme)
    this.markActive(theme)
  }

  applyResolved() {
    const pref = document.documentElement.dataset.themePreference
    const resolved = pref === "auto"
      ? (this.mediaQuery.matches ? "dark" : "light")
      : pref
    const previous = document.documentElement.getAttribute("data-bs-theme")
    document.documentElement.setAttribute("data-bs-theme", resolved)
    if (previous !== resolved) {
      // Charts (Highcharts + lightweight-charts) listen for this and re-read
      // colours from CSS variables on the document element.
      document.dispatchEvent(new CustomEvent("theme:changed", {
        detail: { theme: resolved }
      }))
    }
  }

  setCookie(theme) {
    const oneYearSeconds = 60 * 60 * 24 * 365
    document.cookie = `theme=${theme}; path=/; max-age=${oneYearSeconds}; SameSite=Lax`
  }

  persistToServer(theme) {
    const csrf = document.querySelector('[name="csrf-token"]')?.content
    fetch("/account/update_theme", {
      method: "PATCH",
      headers: {
        "Content-Type": "application/x-www-form-urlencoded",
        "X-CSRF-Token": csrf || "",
        "Accept": "text/html"
      },
      body: `theme=${encodeURIComponent(theme)}`,
      redirect: "manual"
    }).catch(err => console.warn("Failed to save theme:", err))
  }

  // Visual "selected" state for navbar items. Form selects manage this
  // themselves; the navbar uses a class to bold the active option.
  markActive(theme) {
    this.element.querySelectorAll("[data-theme-theme-param]").forEach(el => {
      el.classList.toggle("active", el.dataset.themeThemeParam === theme)
    })
  }
}
